// ============================================================================
// Authenticated session coordinator (retained legacy name)
// ============================================================================
//
// App uses this module only to serialize authenticated session epochs and fence
// action-time work. The former all-data loader is retired: one authenticated
// subject gets one epoch, concurrent intake events for that subject join one
// run, and every async continuation must present the lease it started with
// before publishing state or issuing a follow-on authenticated write.
//
// This is deliberately framework-free. React owns rendering and Supabase owns
// transport; the coordinator owns neither. Keeping it pure makes the identity
// transition rules testable without mounting App's former global boot shell.
// ============================================================================

export type LegacyBootUser = {
  id: string;
};

export type LegacyBootLease = {
  /** Monotonic authenticated-session generation. */
  sessionEpoch: number;
  /** Monotonic only within `sessionEpoch`; a Retry supersedes the prior run. */
  runId: number;
  /** The subject whose profile and authenticated session state this run may publish. */
  userId: string;
  /** Cancel transport where the underlying client supports AbortSignal. */
  signal: AbortSignal | null;
  /** False after sign-out, subject replacement, retry supersession, or abort. */
  isCurrent: () => boolean;
  /**
   * Publish a synchronous state change only while this exact epoch/run owns
   * the session. Abort is an optimisation; this guard is the correctness
   * boundary because a provider can resolve after receiving an abort.
   */
  commit: (sink: () => void) => boolean;
};

export type LegacyBootRunner<T = unknown> = (lease: LegacyBootLease) => T | Promise<T>;

export type LegacyBootStart<T = unknown> = {
  lease: LegacyBootLease;
  promise: Promise<T>;
  /** True when this call joined an already-running or already-settled epoch. */
  joined: boolean;
  /** True only when a new authenticated session epoch was opened. */
  openedSession: boolean;
  /** True only when Retry replaced a run in the current session epoch. */
  retried: boolean;
};

export type LegacyBootCoordinatorState = {
  sessionEpoch: number;
  userId: string | null;
  runId: number;
  running: boolean;
};

export type LegacyBootEnd = {
  sessionEpoch: number;
  ended: boolean;
};

export type AbortControllerLike = {
  signal: AbortSignal;
  abort: () => void;
};

export type LegacyBootCoordinatorDependencies = {
  createAbortController?: () => AbortControllerLike | null;
};

export type LegacyBootCoordinator = {
  /**
   * Accept an authenticated session event. Same-subject events join the
   * current epoch; a changed subject opens a new epoch and invalidates the
   * old lease before its runner can commit again.
   */
  acceptSession: <T>(user: LegacyBootUser, runner: LegacyBootRunner<T>) => LegacyBootStart<T>;
  /**
   * Supersede the current run for this subject in the same epoch. This is the
   * explicit Retry path; it is intentionally never implicit in a token
   * refresh or duplicate INITIAL_SESSION event.
   */
  retry: <T>(user: LegacyBootUser, runner: LegacyBootRunner<T>) => LegacyBootStart<T>;
  /** Idempotently invalidate the active epoch before auth/readiness reset. */
  endSession: () => LegacyBootEnd;
  /** The current lease remains useful to authenticated mutation maintenance. */
  getCurrentLease: () => LegacyBootLease | null;
  getState: () => LegacyBootCoordinatorState;
};

type ActiveRun = {
  sessionEpoch: number;
  runId: number;
  userId: string;
  controller: AbortControllerLike | null;
  lease: LegacyBootLease;
  promise: Promise<unknown> | null;
  running: boolean;
};

function defaultCreateAbortController(): AbortControllerLike | null {
  const Controller = globalThis.AbortController;
  return typeof Controller === 'function' ? new Controller() : null;
}

function validUserId(user: LegacyBootUser | null | undefined): string {
  const id = user && user.id;
  if (typeof id !== 'string' || id.length === 0) throw new TypeError('legacy boot requires an authenticated user id');
  return id;
}

/**
 * Build an epoch/run coordinator. There is no module-level mutable state: a
 * newly mounted App receives a new coordinator and no lease can leak across a
 * page reload.
 */
export function createLegacyBootCoordinator(
  dependencies: LegacyBootCoordinatorDependencies = {},
): LegacyBootCoordinator {
  const createAbortController = dependencies.createAbortController || defaultCreateAbortController;
  let sessionEpoch = 0;
  let runId = 0;
  let active: ActiveRun | null = null;

  function isCurrent(run: ActiveRun): boolean {
    return active === run && !run.controller?.signal.aborted;
  }

  function abort(run: ActiveRun | null): void {
    if (!run) return;
    try {
      run.controller?.abort();
    } catch {
      // Abort is best-effort transport cancellation. The lease guard above is
      // still what prevents a late provider completion from publishing state.
    }
  }

  function startRun<T>(
    userId: string,
    runner: LegacyBootRunner<T>,
    openedSession: boolean,
    retried: boolean,
  ): LegacyBootStart<T> {
    const controller = createAbortController();
    const record = {} as ActiveRun;
    const lease: LegacyBootLease = {
      sessionEpoch,
      runId,
      userId,
      signal: controller ? controller.signal : null,
      isCurrent: () => isCurrent(record),
      commit: (sink) => {
        if (!isCurrent(record)) return false;
        if (typeof sink !== 'function') throw new TypeError('legacy boot commit requires a function');
        sink();
        return true;
      },
    };
    record.sessionEpoch = sessionEpoch;
    record.runId = runId;
    record.userId = userId;
    record.controller = controller;
    record.lease = lease;
    record.promise = null;
    record.running = true;
    active = record;

    // Start after `active` is assigned so a synchronous runner can safely ask
    // whether its lease is current. Promise.resolve().then also converts a
    // synchronous runner throw into the same promise contract as an async one.
    const promise = Promise.resolve().then(() => runner(lease));
    record.promise = promise;
    promise.then(
      () => {
        if (active === record) record.running = false;
      },
      () => {
        if (active === record) record.running = false;
      },
    );

    return {
      lease,
      promise,
      joined: false,
      openedSession,
      retried,
    };
  }

  function openSession<T>(userId: string, runner: LegacyBootRunner<T>): LegacyBootStart<T> {
    abort(active);
    sessionEpoch += 1;
    runId = 1;
    return startRun(userId, runner, true, false);
  }

  function acceptSession<T>(user: LegacyBootUser, runner: LegacyBootRunner<T>): LegacyBootStart<T> {
    const userId = validUserId(user);
    if (!active || active.userId !== userId) return openSession(userId, runner);

    // Same user and current session: INITIAL_SESSION, getSession, token refresh
    // and visibility refresh all receive the same exact promise. Once settled,
    // it remains the settled epoch promise until an explicit Retry or sign-out;
    // event noise cannot re-run the authenticated session chain.
    return {
      lease: active.lease,
      promise: active.promise as Promise<T>,
      joined: true,
      openedSession: false,
      retried: false,
    };
  }

  function retry<T>(user: LegacyBootUser, runner: LegacyBootRunner<T>): LegacyBootStart<T> {
    const userId = validUserId(user);
    if (!active || active.userId !== userId) return openSession(userId, runner);

    abort(active);
    runId += 1;
    return startRun(userId, runner, false, true);
  }

  function endSession(): LegacyBootEnd {
    if (!active) return {sessionEpoch, ended: false};
    abort(active);
    active = null;
    // Advancing on the close makes a same-subject sign-out/sign-in a distinct
    // generation even before the next accepted session opens its own epoch.
    sessionEpoch += 1;
    runId = 0;
    return {sessionEpoch, ended: true};
  }

  function getCurrentLease(): LegacyBootLease | null {
    return active && active.lease.isCurrent() ? active.lease : null;
  }

  function getState(): LegacyBootCoordinatorState {
    return {
      sessionEpoch,
      userId: active ? active.userId : null,
      runId: active ? active.runId : 0,
      running: Boolean(active && active.running),
    };
  }

  return {acceptSession, retry, endSession, getCurrentLease, getState};
}

export default createLegacyBootCoordinator;
